import { useEffect, useState } from "react";
import ClientLayout from "../components/ClientLayout";
import {
  CreditCard,
  FileText,
  TicketIcon,
  AlertCircle,
  CheckCircle,
  Upload,
  Image,
  CircleDollarSign,
  Calendar,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { API, API_FOR_IMG } from "../App";
import { useNavigate } from "react-router-dom";

export default function ClientDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [qrUrl, setQrUrl] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  const [referenceNumber, setReferenceNumber] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("gcash");
  const [refChecking, setRefChecking] = useState(false);
  const [refExists, setRefExists] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  // useEffect(() => {
  //   if (!sessionId) return;

  //   const ws = new WebSocket(`ws://localhost:8000/ws/payment/${sessionId}`);

  //   ws.onmessage = (event) => {
  //     const data = JSON.parse(event.data);

  //     if (data.status === "paid") {
  //       setQrOpen(false); // 🔥 AUTO CLOSE
  //     }
  //   };

  //   return () => ws.close();
  // }, [sessionId]);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/client-portal/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate("/client/login");
        return;
      }
      toast.error("Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </ClientLayout>
    );
  }

  const client = data?.client || {};
  const latestSoa = data?.latest_soa;
  const recentPayments = data?.recent_payments || [];
  const balance = data?.balance || 0;
  const fee = balance * 0.02; // 20% processing fee

  // Helper function to format date to MMM-dd-yyyy (e.g., Jan-01-2026)
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      // If already in MMM-dd-yyyy format, return as is
      if (/^[A-Z][a-z]{2}-\d{2}-\d{4}/.test(dateStr))
        return dateStr.split(" ")[0];
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = months[date.getMonth()];
      const day = String(date.getDate()).padStart(2, "0");
      const year = date.getFullYear();
      return `${month}-${day}-${year}`;
    } catch {
      return dateStr;
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("JPG at PNG files lang ang tinatanggap");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Maximum file size ay 5MB");
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleGCashPayment = async (client) => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${API}/create-payment/${client.id}`,
        {
          amount: balance,
          return_url: `${window.location.origin}/client/payment-success`,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // Redirect to GCash / PayMongo checkout URL
      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error("Unable to initiate GCash payment");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Payment failed");
    }
  };

  const handleQRPhPayment = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await axios.post(
        `${API}/create-qrph-payment`,
        {
          amount: balance,
          client_id: client.id,
          success_url: `${window.location.origin}/client/payment-success`,
          cancel_url: `${window.location.origin}/client/payment-cancel`,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // setQrUrl(res.data.checkout_url);
      // setSessionId(res.data.session_id);
      // setQrOpen(true);
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error("Failed to generate QRPh");
    }
  };

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
    console.log("Selected payment method:", method);
  };
  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Pumili ng file para i-upload");
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem("token");

      // Convert file to base64
      const reader = new FileReader();
      reader.readAsDataURL(selectedFile);
      reader.onload = async () => {
        try {
          await axios.post(
            `${API}/upload-payment-proof`,
            {
              file_data: reader.result,
              filename: selectedFile.name,
              reference_number: referenceNumber,
              payment_method: paymentMethod,
              soa_number: latestSoa?.soa_number,
            },
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );

          toast.success("Payment proof uploaded successfully!");
          setUploadOpen(false);
          setSelectedFile(null);
          setPreviewUrl(null);
          fetchDashboard();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Failed to upload");
        } finally {
          setUploading(false);
        }
      };
    } catch (error) {
      toast.error("Failed to process file");
      setUploading(false);
    }
  };

  return (
    <ClientLayout>
      <div className="p-6 lg:p-8">
        <div className="mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">
            Welcome, {client.full_name}!
          </h1>
          <p className="text-slate-600 mt-1">
            Account #{client.account_number}
          </p>
        </div>

        {/* Balance Card */}
        <div
          className={`rounded-2xl p-6 mb-8 ${balance > 0 ? "bg-gradient-to-br from-red-500 to-red-600" : "bg-gradient-to-br from-emerald-500 to-emerald-600"}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm mb-1">Current Balance</p>
              <p className="text-4xl font-bold text-white">
                ₱{balance.toLocaleString()}
              </p>
              <p className="text-white/80 text-sm mt-2">
                {balance > 0 ? (
                  <span className="flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    Payment required
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    All caught up!
                  </span>
                )}
              </p>
            </div>

            <div className="flex flex-col gap-2 items-end">
              <CreditCard className="w-16 h-16 text-white/30" />

              {balance > 0 && (
                <div className="flex flex-col gap-2">
                  {/* <Button
                    onClick={() => handleGCashPayment(client)}
                    className="bg-blue-500 hover:bg-blue-600 text-white border-0 flex justify-between"
                    size="sm"
                  >
                    <CircleDollarSign className="w-4 h-4 mr-2" />
                    Pay with GCash
                  </Button> */}

                  <Button
                    onClick={handleQRPhPayment}
                    className="bg-indigo-500 hover:bg-indigo-600 text-white flex justify-between"
                  >
                    <CircleDollarSign className="w-4 h-4 mr-2" />
                    Pay via QRPh
                  </Button>

                  <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                    <DialogTrigger asChild>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="bg-white/20 hover:bg-white/30 text-white border-0"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Payment Proof
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden">
                      <DialogHeader className="top-0 z-20 bg-white px-6 py-4 border-b">
                        <DialogTitle>Upload Payment Proof</DialogTitle>
                      </DialogHeader>

                      <div className="px-6 pb-6">
                        <div className="space-y-4">
                          <div>
                            <Label>Select Image (JPG or PNG, max 5MB)</Label>
                            <Input
                              type="file"
                              accept=".jpg,.jpeg,.png"
                              onChange={handleFileSelect}
                              className="mt-2"
                              data-testid="proof-file-input"
                            />
                          </div>
                          {previewUrl && (
                            <div className="border rounded-lg p-2">
                              <p className="text-sm text-slate-500 mb-2">
                                Preview:
                              </p>
                              <img
                                src={previewUrl}
                                alt="Payment proof preview"
                                className="max-h-48 mx-auto rounded"
                              />
                            </div>
                          )}
                          <div>
                            <Label>Payment Method</Label>
                            <Select
                              value={paymentMethod}
                              onValueChange={(val) =>
                                handlePaymentMethodChange(val)
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {/* <SelectItem value="cash">Cash</SelectItem> */}
                                <SelectItem value="gcash">GCash</SelectItem>
                                <SelectItem value="credit_card">
                                  Credit Card
                                </SelectItem>
                                <SelectItem value="bank_transfer">
                                  Bank Transfer
                                </SelectItem>
                                <SelectItem value="others">Others</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label>Reference Number</Label>
                            <Input
                              value={referenceNumber}
                              onChange={(e) =>
                                setReferenceNumber(e.target.value)
                              }
                              placeholder="Enter reference number"
                              className="mt-2"
                            />

                            {refChecking && (
                              <p className="text-xs text-gray-500 mt-1">
                                Checking...
                              </p>
                            )}

                            {refExists && (
                              <p className="text-xs text-red-500 mt-1">
                                Reference number already exists!
                              </p>
                            )}
                          </div>

                          <Button
                            onClick={handleUpload}
                            disabled={
                              !selectedFile ||
                              uploading ||
                              refExists ||
                              !referenceNumber
                            }
                            className="w-full"
                            data-testid="submit-proof-btn"
                          >
                            {uploading ? "Uploading..." : "Upload Proof"}
                          </Button>
                          <p className="text-xs text-slate-500 text-center">
                            Mag-upload ng screenshot o photo ng iyong payment
                            receipt (GCash, bank transfer, etc.)
                          </p>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            </div>
          </div>
          {client.payment_proof_url && (
            <div className="mt-4 pt-4 border-t border-white/20">
              <p className="text-white/80 text-sm flex items-center gap-2">
                <Image className="w-4 h-4" />
                Payment proof uploaded:{" "}
                {client.payment_proof_uploaded_at || "Recently"}
              </p>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Monthly Plan</p>
                <p className="text-lg font-bold text-slate-900">
                  {client.plan_name}
                </p>
                <p className="text-sm text-slate-500">
                  ₱{client.monthly_fee}/mo
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Cut-off Day</p>
                <p className="text-lg font-bold text-slate-900">
                  Every {client.cut_off_day || 1}
                  {["st", "nd", "rd"][(client.cut_off_day || 1) - 1] || "th"}
                </p>
                <p className="text-sm text-slate-500">of the month</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Due Date</p>
                <p className="text-lg font-bold text-slate-900">
                  {client.due_date || 1}
                  {["st", "nd", "rd"][(client.due_date || 1) - 1]}
                </p>
                {/* <p className="text-sm text-slate-500">of the month</p> */}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center">
                <TicketIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Open Tickets</p>
                <p className="text-lg font-bold text-slate-900">
                  {data?.open_tickets || 0}
                </p>
                <p className="text-sm text-slate-500">support requests</p>
              </div>
            </div>
          </div>
        </div>

        {/* Disconnection Warning */}
        {client.disconnection_date && balance > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-8">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-red-600" />
              <div>
                <p className="font-semibold text-red-800">
                  Disconnection Warning
                </p>
                <p className="text-sm text-red-600">
                  Your service will be disconnected on{" "}
                  <span className="font-bold">{client.disconnection_date}</span>{" "}
                  if payment is not received.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Latest SOA */}
        {latestSoa && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-8">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-slate-900">
                  Latest Statement of Account
                </h2>
                <p className="text-sm text-slate-500">
                  SOA #{latestSoa.soa_number}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/client/soa/${latestSoa.id}`)}
              >
                View Details
              </Button>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Billing Period</p>
                  <p className="font-medium text-slate-900">
                    {formatDate(latestSoa.billing_period_start)} -{" "}
                    {formatDate(latestSoa.billing_period_end)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Previous Balance</p>
                  <p className="font-medium text-slate-900">
                    ₱{latestSoa.previous_balance.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Current Charges</p>
                  <p className="font-medium text-slate-900">
                    ₱{latestSoa.current_charges.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Due</p>
                  <p className="font-bold text-lg text-red-600">
                    ₱{latestSoa.total_amount_due.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recent Payments */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Recent Payments</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/client/payments")}
            >
              View All
            </Button>
          </div>
          <div className="divide-y divide-slate-100">
            {recentPayments.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                No recent payments
              </div>
            ) : (
              recentPayments.map((payment) => (
                <div
                  key={payment.id}
                  className="p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    {payment.proof_image_url && (
                      <a
                        href={
                          payment.proof_image_url.startsWith("http")
                            ? payment.proof_image_url
                            : `${API_FOR_IMG}${payment.proof_image_url}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 rounded-lg overflow-hidden border border-slate-200 flex-shrink-0"
                      >
                        <img
                          src={
                            payment.proof_image_url.startsWith("http")
                              ? payment.proof_image_url
                              : `${API_FOR_IMG}${payment.proof_image_url}`
                          }
                          alt="Payment proof"
                          className="w-full h-full object-cover"
                        />
                      </a>
                    )}
                    <div>
                      <p className="font-medium text-slate-900">
                        ₱{payment.amount.toLocaleString()}
                      </p>
                      <p className="text-sm text-slate-500">
                        {payment.payment_method} •{" "}
                        {payment.reference_number || "N/A"}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500">
                    {formatDate(payment.created_at)}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="max-w-2xl overflow-hidden">
          <DialogHeader className="top-0 z-20 bg-white px-6 py-4 border-b">
            <DialogTitle>Scan QR to Pay</DialogTitle>
          </DialogHeader>

          <div className="px-6 pb-6">
            <p className="text-left text-sm text-slate-500 mt-2">
              Amount: ₱{parseFloat(balance).toFixed(2)}
              <br />
              Processing Fee: ₱{parseFloat(fee).toFixed(2)}
            </p>

            {/* <p className="text-left text-sm text-slate-500">
            Processing Fee: ₱{fee}
          </p> */}

            <p className="text-left font-semibold">
              Total: ₱{parseFloat(balance + fee).toFixed(2)}
            </p>

            <iframe
              src={qrUrl}
              className="w-full h-[500px] rounded-lg border"
            />

            <p className="text-center text-sm text-slate-500 mt-2">
              Scan using GCash, Maya, or any banking app
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </ClientLayout>
  );
}
